import { useState } from 'react';

export default function LoginPage({ onLogin }) {
  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Inloggen mislukt'); return; }
      const expiry = keepLoggedIn
        ? Date.now() + 30 * 24 * 60 * 60 * 1000
        : Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_expiry', String(expiry));
      onLogin();
    } catch {
      setError('Kan geen verbinding maken met de server');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--md-background)',
      padding: '24px 16px',
    }}>
      {/* Background decoration — subtle M3 tonal blob */}
      <div style={{
        position: 'fixed',
        top: '-20vh',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '120vw',
        height: '60vh',
        background: 'radial-gradient(ellipse, var(--md-primary-container) 0%, transparent 70%)',
        opacity: 0.35,
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        background: 'var(--md-surface-container)',
        borderRadius: 'var(--ou-radius-xl)',
        padding: '40px 32px 36px',
        width: '100%',
        maxWidth: 400,
        boxShadow: 'var(--md-elev-3)',
      }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 72,
            height: 72,
            borderRadius: 'var(--ou-radius-xl)',
            background: 'var(--md-primary-container)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 36,
            marginBottom: 20,
          }}>
            🧊
          </div>
          <h1 style={{
            fontFamily: 'var(--md-font-heading)',
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--md-on-surface)',
            letterSpacing: '-0.02em',
            marginBottom: 6,
          }}>
            Freezer by Jonas
          </h1>
          <p style={{ color: 'var(--md-on-surface-variant)', fontSize: 15 }}>
            Log in om door te gaan
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Username field */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--md-on-surface-variant)',
              letterSpacing: '0.02em',
            }}>
              Gebruikersnaam
            </label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              style={{
                background: 'var(--md-surface-container-high)',
                border: '1px solid var(--md-outline)',
                borderRadius: 'var(--ou-radius-md)',
                padding: '14px 16px',
                color: 'var(--md-on-surface)',
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                WebkitUserSelect: 'text',
                userSelect: 'text',
                width: '100%',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--md-primary)'; e.target.style.borderWidth = '2px'; e.target.style.padding = '13px 15px'; }}
              onBlur={e  => { e.target.style.borderColor = 'var(--md-outline)'; e.target.style.borderWidth = '1px'; e.target.style.padding = '14px 16px'; }}
            />
          </div>

          {/* Password field */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--md-on-surface-variant)',
              letterSpacing: '0.02em',
            }}>
              Wachtwoord
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                background: 'var(--md-surface-container-high)',
                border: '1px solid var(--md-outline)',
                borderRadius: 'var(--ou-radius-md)',
                padding: '14px 16px',
                color: 'var(--md-on-surface)',
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                WebkitUserSelect: 'text',
                userSelect: 'text',
                width: '100%',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--md-primary)'; e.target.style.borderWidth = '2px'; e.target.style.padding = '13px 15px'; }}
              onBlur={e  => { e.target.style.borderColor = 'var(--md-outline)'; e.target.style.borderWidth = '1px'; e.target.style.padding = '14px 16px'; }}
            />
          </div>

          {/* Keep logged in toggle */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
            userSelect: 'none',
            padding: '4px 0',
          }}>
            <input
              type="checkbox"
              checked={keepLoggedIn}
              onChange={e => setKeepLoggedIn(e.target.checked)}
              style={{ accentColor: 'var(--md-primary)', width: 18, height: 18, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 14, color: 'var(--md-on-surface-variant)' }}>
              Ingelogd blijven (30 dagen)
            </span>
          </label>

          {/* Error */}
          {error && (
            <div style={{
              background: 'var(--md-error-container)',
              borderRadius: 'var(--ou-radius-md)',
              padding: '12px 16px',
              fontSize: 14,
              color: 'var(--md-on-error-container)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500,
            }}>
              <span>⚠</span>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? 'var(--md-surface-container-high)' : 'var(--md-primary)',
              color: loading ? 'var(--md-on-surface-variant)' : 'var(--md-on-primary)',
              border: 'none',
              borderRadius: 'var(--ou-radius-full)',
              padding: '0 24px',
              height: 52,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: 'var(--md-font-body)',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              transition: 'all 0.2s',
              width: '100%',
            }}
          >
            {loading && (
              <span style={{
                width: 18,
                height: 18,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin 0.7s linear infinite',
              }} />
            )}
            {loading ? 'Inloggen...' : 'Inloggen'}
          </button>
        </form>
      </div>
    </div>
  );
}
